"use client";

import React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

interface DynamicContentSectionProps {
    contentKey?: string;
    layout?: 'image-left' | 'image-right';
    showCta?: boolean;
    customImageClass?: string;
    image1Src?: string;
    image1Alt?: string;
    image2Src?: string;
    image2Alt?: string;
    showSingleImage?: boolean;
    showBottomLine?: boolean; // New prop to control bottom line visibility
    experience?: {
        years: string;
        label1: string;
        label2: string;
    };
}

export const DynamicContentSection: React.FC<DynamicContentSectionProps> = ({ 
    contentKey = 'About.hero',
    layout = 'image-right',
    showCta = true,
    customImageClass = '',
    image1Src = '/images/gallery-section/ciscode-image-about-2.png',
    image1Alt = 'Team collaboration',
    image2Src = '/images/gallery-section/ciscode-image-about-1.png',
    image2Alt = 'Technology solutions',
    showSingleImage = false,
    showBottomLine = true, // Default to true to maintain backward compatibility
    experience
}) => {
    const t = useTranslations(contentKey);
    const isImageLeft = layout === 'image-left';
    
    return (
        <section className="relative overflow-hidden bg-white">
            <div className="container mx-auto py-8 md:py-20 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px] lg:min-h-[700px] gap-y-8 lg:gap-12">
                    
                    {/* Conditional rendering based on layout */}
                    {isImageLeft ? (
                        <>
                            {/* Left Column - Images */}
                            <div className="relative bg-white flex items-center justify-center lg:justify-start">
                                <div className="relative w-full h-full min-h-[300px] sm:min-h-[400px] md:min-h-[500px] lg:min-h-[500px]">
                                    {showSingleImage ? (
                                        // Single image layout for TeamDrivenSection
                                        <div 
                                            className="absolute inset-0 rounded-lg overflow-hidden translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1000ms] group hover:scale-105 transition-transform duration-500"
                                        >
                                            <img 
                                                src={image1Src}
                                                alt={image1Alt}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            {/* Gradient Overlay on hover */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                        </div>
                                    ) : (
                                        // Double image layout (original)
                                        <>
                                            {/* Image 1 - Top Right */}
                                            <div 
                                                className="absolute top-0 right-0 rounded-sm overflow-hidden w-[300px] h-[220px] sm:w-[360px] sm:h-[260px] md:w-[500px] md:h-[300px] lg:w-[524px] lg:h-[377px] translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1000ms] group hover:scale-105 transition-transform duration-500"
                                            >
                                                <img 
                                                    src={image1Src}
                                                    alt={image1Alt}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                            
                                            {/* Image 2 - Bottom Left */}
                                            <div 
                                                className="absolute bottom-0 left-0 rounded-sm overflow-hidden w-[280px] h-[200px] sm:w-[360px] sm:h-[240px] md:w-[500px] md:h-[300px] lg:w-[460px] lg:h-[380px] translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1200ms] group hover:scale-105 transition-transform duration-500"
                                            >
                                                <img 
                                                    src={image2Src}
                                                    alt={image2Alt}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Experience Card - Bottom Right (only shown when not in single image mode) */}
                                    {!showSingleImage && experience && (
                                        <div className="absolute bottom-2 sm:bottom-4 md:bottom-6 lg:bottom-6 right-0 z-10 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1400ms]">
                                            <div 
                                                className="rounded-sm shadow-xl w-[180px] h-[80px] sm:w-[220px] sm:h-[95px] md:w-[260px] md:h-[110px] lg:w-[311px] lg:h-[138px] bg-brand-gradient p-2 sm:p-3 md:p-3 lg:p-4 hover:scale-105 hover:shadow-2xl transition-all duration-300 group"
                                            >
                                                {/* Dashed border container with padding */}
                                                <div className="w-full h-full border-2 border-dashed border-x-200 flex items-center justify-center group-hover:border-white/50 transition-colors duration-300">
                                                    <div className="flex items-center gap-2 sm:gap-3 md:gap-3 lg:gap-4">
                                                        {/* Number */}
                                                        <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                                                            {experience.years}
                                                        </div>
                                                        
                                                        {/* Text */}
                                                        <div className="text-white font-button text-button font-button">
                                                            <div className="leading-tight text-xs sm:text-sm md:text-sm lg:text-lg group-hover:translate-y-[-2px] transition-transform duration-300">
                                                                {experience.label1}
                                                            </div>
                                                            <div className="leading-tight text-xs sm:text-sm md:text-sm lg:text-lg group-hover:translate-y-[-2px] transition-transform duration-300">
                                                                {experience.label2}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column - Content */}
                            <div className="relative bg-white flex flex-col justify-start">
                                <div className="max-w-lg mx-auto lg:mx-0">
                                    {/* Header */}
                                    <div className="text-xs sm:text-sm md:text-lg lg:text-h1-up font-h1-up uppercase mb-2 sm:mb-3 md:mb-4 lg:mb-4 bg-brand-gradient-text text-center md:text-left lg:text-left translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
                                        {t('tagline')}
                                    </div>

                                    {/* Main Title */}
                                    <h1 className="text-lg sm:text-xl md:text-3xl lg:text-heading-h1 font-heading-h1 mb-3 sm:mb-4 md:mb-5 lg:mb-6 text-x-900 text-center md:text-left lg:text-left leading-tight translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
                                        {t('title')}
                                    </h1>

                                    {/* Description */}
                                    <p className="text-sm sm:text-base md:text-lg lg:text-body-m font-body-m font-body-m mb-4 sm:mb-6 md:mb-7 lg:mb-8 text-x-800 text-center md:text-left lg:text-left leading-relaxed translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
                                        {t('description')}
                                    </p>

                                    {/* CTA Button */}
                                    {showCta && (
                                        <div className="flex justify-center md:justify-start lg:justify-start">
                                            <Button 
                                                asChild
                                                className="group inline-flex items-center gap-2 sm:gap-3 md:gap-3 lg:gap-3 text-white [&_*]:text-white font-button text-button font-button transition-all duration-300 transform hover:scale-105 hover:shadow-xl bg-brand-gradient h-10 sm:h-12 md:h-14 lg:h-[60px] px-4 sm:px-6 md:px-8 lg:px-[30px] py-2 sm:py-3 md:py-4 lg:py-[20px] rounded-lg text-sm sm:text-base md:text-lg lg:text-button translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:800ms] shadow-lg"
                                            >
                                                <a href="#about">
                                                    <span className="text-white">{t('cta.text')}</span>
                                                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-5 lg:h-5 group-hover:translate-x-1 transition-transform duration-300 text-white" />
                                                </a>
                                            </Button>
                                        </div>
                                    )}
                                    
                                    {/* Bottom Line - Highlighted Text (only shown in single image mode and when showBottomLine is true) */}
                                    {showSingleImage && showBottomLine && t('bottomLine') && (
                                        <div className="relative pt-6 mt-4 border-t-2 border-brand-secondary/20">
                                            <p className="text-base sm:text-lg md:text-xl lg:text-heading-h3 font-heading-h3 font-heading-h3 font-bold text-brand-primary">
                                                {t('bottomLine')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Left Column - Content */}
                            <div className="relative bg-white flex flex-col justify-start">
                                <div className="max-w-lg mx-auto lg:mx-0">
                                    {/* Header */}
                                    <div className="text-xs sm:text-sm md:text-lg lg:text-h1-up font-h1-up uppercase mb-2 sm:mb-3 md:mb-4 lg:mb-4 bg-brand-gradient-text text-center md:text-left lg:text-left translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
                                        {t('tagline')}
                                    </div>

                                    {/* Main Title */}
                                    <h1 className="text-lg sm:text-xl md:text-3xl lg:text-heading-h1 font-heading-h1 mb-3 sm:mb-4 md:mb-5 lg:mb-6 text-x-900 text-center md:text-left lg:text-left leading-tight translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
                                        {t('title')}
                                    </h1>

                                    {/* Description */}
                                    <p className="text-sm sm:text-base md:text-lg lg:text-body-m font-body-m font-body-m mb-4 sm:mb-6 md:mb-7 lg:mb-8 text-x-800 text-center md:text-left lg:text-left leading-relaxed translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
                                        {t('description')}
                                    </p>

                                    {/* CTA Button */}
                                    {showCta && (
                                        <div className="flex justify-center md:justify-start lg:justify-start">
                                            <Button 
                                                asChild
                                                className="group inline-flex items-center gap-2 sm:gap-3 md:gap-3 lg:gap-3 text-white [&_*]:text-white font-button text-button font-button transition-all duration-300 transform hover:scale-105 hover:shadow-xl bg-brand-gradient h-10 sm:h-12 md:h-14 lg:h-[60px] px-4 sm:px-6 md:px-8 lg:px-[30px] py-2 sm:py-3 md:py-4 lg:py-[20px] rounded-lg text-sm sm:text-base md:text-lg lg:text-button translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:800ms] shadow-lg"
                                            >
                                                <a href="#about">
                                                    <span className="text-white">{t('cta.text')}</span>
                                                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-5 lg:h-5 group-hover:translate-x-1 transition-transform duration-300 text-white" />
                                                </a>
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column - Images */}
                            <div className="relative bg-white flex items-center justify-center lg:justify-start">
                                <div className="relative w-full h-full min-h-[300px] sm:min-h-[400px] md:min-h-[500px] lg:min-h-[500px]">
                                    {showSingleImage ? (
                                        // Single image layout
                                        <div 
                                            className="absolute inset-0 rounded-lg overflow-hidden translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1000ms] group hover:scale-105 transition-transform duration-500"
                                        >
                                            <img 
                                                src={image1Src}
                                                alt={image1Alt}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            {/* Gradient Overlay on hover */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                        </div>
                                    ) : (
                                        // Double image layout (original)
                                        <>
                                            {/* Image 1 - Top Right */}
                                            <div 
                                                className="absolute top-0 right-0 rounded-sm overflow-hidden w-[300px] h-[220px] sm:w-[360px] sm:h-[260px] md:w-[500px] md:h-[300px] lg:w-[524px] lg:h-[377px] translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1000ms] group hover:scale-105 transition-transform duration-500"
                                            >
                                                <img 
                                                    src={image1Src}
                                                    alt={image1Alt}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                            
                                            {/* Image 2 - Bottom Left */}
                                            <div 
                                                className="absolute bottom-0 left-0 rounded-sm overflow-hidden w-[280px] h-[200px] sm:w-[360px] sm:h-[240px] md:w-[500px] md:h-[300px] lg:w-[460px] lg:h-[380px] translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1200ms] group hover:scale-105 transition-transform duration-500"
                                            >
                                                <img 
                                                    src={image2Src}
                                                    alt={image2Alt}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Experience Card - Bottom Right (only shown when not in single image mode) */}
                                    {!showSingleImage && experience && (
                                        <div className="absolute bottom-2 sm:bottom-4 md:bottom-6 lg:bottom-6 right-0 z-10 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:1400ms]">
                                            <div 
                                                className="rounded-sm shadow-xl w-[180px] h-[80px] sm:w-[220px] sm:h-[95px] md:w-[260px] md:h-[110px] lg:w-[311px] lg:h-[138px] bg-brand-gradient p-2 sm:p-3 md:p-3 lg:p-4 hover:scale-105 hover:shadow-2xl transition-all duration-300 group"
                                            >
                                                {/* Dashed border container with padding */}
                                                <div className="w-full h-full border-2 border-dashed border-x-200 flex items-center justify-center group-hover:border-white/50 transition-colors duration-300">
                                                    <div className="flex items-center gap-2 sm:gap-3 md:gap-3 lg:gap-4">
                                                        {/* Number */}
                                                        <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                                                            {experience.years}
                                                        </div>
                                                        
                                                        {/* Text */}
                                                        <div className="text-white font-button text-button font-button">
                                                            <div className="leading-tight text-xs sm:text-sm md:text-sm lg:text-lg group-hover:translate-y-[-2px] transition-transform duration-300">
                                                                {experience.label1}
                                                            </div>
                                                            <div className="leading-tight text-xs sm:text-sm md:text-sm lg:text-lg group-hover:translate-y-[-2px] transition-transform duration-300">
                                                                {experience.label2}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            
            {/* Background Decorative Elements (only shown in single image mode) */}
            {showSingleImage && (
                <>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gradient opacity-5 blur-3xl rounded-full"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500 opacity-5 blur-3xl rounded-full"></div>
                </>
            )}
        </section>
    );
};